'use client';

import {
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Modal,
  ModalBody,
  ModalContent,
  useDisclosure,
} from '@nextui-org/react';
import mixpanel from 'mixpanel-browser';
import Link from 'next/link';
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { toast } from 'react-toastify';

import { CloseLineHeaderIcon, IconCardTemplateEye, IconOptionCardTemplateProfile } from '@/assets';
import { Images } from '@/components';
import { IconHeartNotLiked, IconRun } from '@/components/assets';
import NoData from '@/components/NoData';
import RenderWhen from '@/components/RenderWhen';
import { MIXPANEL_TRACK } from '@/configs/constant';
import useWindowSize from '@/hooks/useWindowSize';
import LoginModal from '@/layouts/header/Login';
import RemixCommon from '@/layouts/remix-common';
import { useRouter } from '@/provider/RouterProvider';
import { getUsecaseById, postIncreaseTotalUse, postUnpublishTemplalte } from '@/services/usecase';
import type { IUsecaseDetail, IUsecaseItem } from '@/types/usecase';
import { formatCash } from '@/utils';

import UsecaseDetail from './[id]/UsecaseDetail';
import FilterUsecase from './FilterUsecase';

const ListUsecaseProfile: React.FC<{
  openFilter?: boolean;
  setOpenFilter?: Dispatch<SetStateAction<boolean>>;
  list: IUsecaseItem[];
  loading: boolean;
  currentPage: number;
  loadMore: (page: number) => void;
  isViewInUrl?: boolean;
}> = ({ openFilter, setOpenFilter, list, loading, currentPage, loadMore, isViewInUrl = false }) => {
  const [listPost, setListPost] = useState<IUsecaseItem[]>(list);
  const { isMobileView } = useWindowSize();
  const router = useRouter();
  const [remixConfig, setRemixConfig] = useState<IUsecaseItem | null>(null);

  const useModalRemix = useDisclosure({
    onClose() {
      document.body.style.removeProperty('overflow');
      setRemixConfig(null);
    },
  });
  const { onOpen } = useModalRemix;
  const { isOpen, onOpenChange, onClose } = useDisclosure();
  const {
    isOpen: isOpenViewTemplate,
    onOpen: onOpenViewTemplate,
    onClose: onCloseViewTemplate,
  } = useDisclosure();

  const [dataTemplateToView, setDataTemplateToView] = useState<IUsecaseDetail>();

  const handleOpenModalViewTemplate = async (id: number) => {
    if (isViewInUrl) {
      router.push(`/template/${id}`);
    } else {
      const resUsecase = await getUsecaseById(id);
      if (resUsecase) {
        setDataTemplateToView(resUsecase.data);
        onOpenViewTemplate();
      }
    }
  };

  const handelUnPublishTemplate = async (templateId: any) => {
    const res = await postUnpublishTemplalte(templateId);
    if (res.data.message === 'success') {
      toast.success('Successfully removed post');
    }
    setListPost(listPost.filter((item) => item.id !== templateId));
  };

  const handleUseUseCase = (status: number, usecaseId: number, remixConfig: IUsecaseItem) => {
    // const urlGoToGenerate = `${MENU_LINK.aiModel}/${modelId}/generate?template=${usecaseId}`;

    postIncreaseTotalUse(usecaseId);
    if (status === 4) {
      return toast.error('Model maintenance!');
    }
    mixpanel.track(MIXPANEL_TRACK.REMIX);
    setRemixConfig(remixConfig);
    document.body.style.overflow = 'hidden';
    onOpen();
  };

  useEffect(() => {
    setListPost(list);
  }, [list]);

  return (
    <div className="flex min-h-[400px] w-full">
      {remixConfig && <RemixCommon useModalRemix={useModalRemix} remixConfig={remixConfig} />}
      <div
        aria-expanded={openFilter}
        className="scrollbar-custom relative w-0 overflow-y-auto opacity-0 duration-300 aria-expanded:w-[260px] aria-expanded:opacity-100 md:fixed md:inset-0 md:z-99 md:bg-white md:pr-0 aria-expanded:md:w-full"
      >
        <div className="hidden w-full items-center justify-between p-4 md:flex">
          <span className="text-20 font-semibold">Filters</span>
          <CloseLineHeaderIcon onClick={() => setOpenFilter && setOpenFilter(false)} />
        </div>
        <Divider className="hidden w-full bg-gray-100 md:block" />
        {openFilter && <FilterUsecase setOpenFilter={setOpenFilter} />}
      </div>
      <div aria-expanded={openFilter} className="w-full aria-expanded:w-[calc(100%-260px)]">
        <RenderWhen>
          <RenderWhen.If isTrue={list.length === 0 && !loading}>
            <NoData />
          </RenderWhen.If>
          <RenderWhen.If isTrue={list.length > 0}>
            <InfiniteScroll
              style={{ overflow: 'hidden' }}
              dataLength={list.length}
              next={() => {
                loadMore(currentPage + 1);
              }}
              hasMore
              loader=""
            >
              <ResponsiveMasonry
                columnsCountBreakPoints={{ 350: 2, 750: 2, 900: 3, 1200: 4, 1800: 5 }}
              >
                <Masonry gutter={isMobileView ? '8px' : '16px'}>
                  {listPost.map((usecase) => (
                    <>
                      <Link
                        key={usecase.id}
                        // onClick={() => {
                        //   router.push(`/template/${usecase.id}`);
                        // }}
                        href={`/template/${usecase.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleOpenModalViewTemplate(usecase.id);
                        }}
                        className="group relative cursor-pointer overflow-hidden rounded-[16px] shadow-lg shadow-[rgba(199,205,231,0.55)]  sm:rounded-[10px]"
                      >
                        <Images
                          src={usecase?.processingAsset?.url}
                          alt="usecase"
                          className="h-full w-full cursor-pointer object-contain duration-300 group-hover:scale-110"
                        />
                        {/* {usecase.model && (
                          <div
                            className="absolute inset-x-2 bottom-2 z-[20] rounded-[12px] bg-[rgba(18,18,18,0.50)] px-3 py-[5px] text-[14px] text-white backdrop-blur-[2px] transition-all sm:hidden"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              router.push(`/ai-model/${usecase.model.id}/generate`);
                            }}
                          >
                            <p className="text-14 font-semibold leading-[25px]">
                              {usecase.model.name}
                            </p>
                          </div>
                        )} */}
                        <div className="absolute bottom-0 left-0 z-10 h-[100px] w-full bg-gradient-to-t from-black/80 to-black/0" />
                        <div className="absolute inset-x-0 bottom-0 z-10 p-3 lg:p-0 lg:pl-2">
                          {usecase.name && (
                            <h6 className="truncate text-[16px] font-medium text-white drop-shadow lg:text-[14px]">
                              {usecase.name}
                            </h6>
                          )}
                          {usecase.description && (
                            <div className="mt-[4px] max-w-[80%] truncate text-[13px] text-white lg:hidden">
                              <span>{usecase.description}</span>
                            </div>
                          )}
                          <div className="mt-[8px] flex items-center justify-between lg:hidden lg:flex-col lg:items-start lg:justify-start lg:gap-1">
                            <div className="flex items-center gap-[6px]">
                              {!!usecase.totalReactions && usecase?.totalReactions > 0 && (
                                <>
                                  <div className="flex items-center">
                                    <IconHeartNotLiked width={16} height={16} color="white" />
                                    <span className="ml-1 text-[12px] text-white">
                                      {formatCash(usecase?.totalReactions)}
                                    </span>
                                  </div>
                                  <span className="h-1 w-1 rounded-full bg-white opacity-60" />
                                </>
                              )}
                              {!!usecase.totalUse && usecase.totalUse > 0 && (
                                <>
                                  <div className="flex items-center">
                                    <IconRun width={12} height={12} color="white" />
                                    <span className="ml-1 text-[12px] text-white">
                                      {formatCash(usecase.totalUse)}
                                    </span>
                                  </div>
                                  <span className="h-1 w-1 rounded-full bg-white opacity-60" />
                                </>
                              )}
                              {usecase?.totalViews > 0 && (
                                <>
                                  <div className="flex items-center">
                                    <IconCardTemplateEye />
                                    <span className="ml-1 text-[12px] text-white">
                                      {formatCash(usecase.totalViews)}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {usecase.model?.isWorkflow ? (
                          <p className="absolute left-2 top-2 z-[10] flex w-fit items-center justify-self-end rounded-[100px] bg-[rgba(18,18,18,0.50)] px-[6px] py-[2px] text-[14px] text-white backdrop-blur-[2px] transition-all sm:hidden">
                            <p className="px-1 text-12 font-semibold leading-5">AI Tool</p>
                          </p>
                        ) : (
                          <p className="absolute left-2 top-2 z-[10] flex w-fit items-center justify-self-end rounded-[100px] bg-[rgba(18,18,18,0.50)] px-[6px] py-[2px] text-[14px] text-white backdrop-blur-[2px] transition-all sm:hidden">
                            <p className="px-1 text-12 font-semibold leading-5">
                              {usecase?.model?.name}
                            </p>
                          </p>
                        )}
                        <div
                          className="absolute right-2 top-1 z-[20]"
                          onClick={(event) => {
                            event.preventDefault();
                          }}
                        >
                          {/* {!usecase.model?.isWorkflow && (
                            <div className="flex items-center rounded-[100px] bg-gradient-to-r from-[#DA613A]  via-[#EB476B]  to-[#7D3DDC] px-2 py-[1px] sm:rounded-[4px] sm:px-1">
                              <p
                                className="flex items-center gap-1 text-[14px] font-semibold text-white sm:text-[13px]"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleUseUseCase(usecase.model.status, usecase.id, usecase);
                                }}
                              >
                                <IconRemixHover className="sm:hidden" /> Remix
                              </p>
                            </div>
                          )} */}
                          <Dropdown>
                            <DropdownTrigger>
                              <div
                                className="flex scale-75 cursor-pointer items-center justify-end pt-1"
                                onClick={(event) => {
                                  event.preventDefault();
                                }}
                              >
                                <IconOptionCardTemplateProfile />
                              </div>
                            </DropdownTrigger>
                            <DropdownMenu
                              aria-label="Static Actions"
                              onAction={(key) => {
                                if (key === 'remix') {
                                  if (usecase.model?.isWorkflow) {
                                    window.open(`/showcase/${usecase.model.id}`, '_blank');
                                  } else {
                                    handleUseUseCase(usecase.model.status, usecase.id, usecase);
                                  }
                                } else {
                                  handelUnPublishTemplate(usecase.id);
                                }
                              }}
                            >
                              <DropdownItem key="remix">
                                {/* {item.hide === false && 'Hide'}
                                  {item.hide === true && 'Unhide'} */}
                                Remix
                              </DropdownItem>

                              {/* <DropdownItem key="edit">Edit</DropdownItem> */}
                              <DropdownItem key="unpublish" className="text-danger" color="danger">
                                Un Publish
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </Link>
                    </>
                  ))}
                </Masonry>
              </ResponsiveMasonry>
            </InfiniteScroll>
          </RenderWhen.If>
        </RenderWhen>
      </div>
      <LoginModal isOpen={isOpen} onClose={onClose} onOpenChange={onOpenChange} />
      {dataTemplateToView && (
        <Modal
          isOpen={isOpenViewTemplate}
          onClose={onCloseViewTemplate}
          classNames={{
            body: 'pt-5 px-5 pb-0 md:p-3 md:pb-0 h-full',
            base: 'max-w-[85%] h-[80%]',
            wrapper: 'items-center',
          }}
          scrollBehavior="inside"
          placement="center"
          hideCloseButton
        >
          <ModalContent>
            {(onCloseViewTemplate) => (
              <>
                <div
                  onClick={onCloseViewTemplate}
                  className="absolute right-[-10px] top-[-10px] z-50 flex h-[28px] w-[28px] cursor-pointer items-center justify-center rounded-full border-3 border-white bg-gray-50"
                >
                  <span>&#10005;</span>
                </div>
                <ModalBody>
                  <UsecaseDetail usecase={dataTemplateToView} isPopUp />
                </ModalBody>
              </>
            )}
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default ListUsecaseProfile;
